import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false); // True during login transition to prevent stale data flash
  const [error, setError] = useState(null);
  // Session key changes on each login to force components to refetch data
  const [sessionKey, setSessionKey] = useState(() => localStorage.getItem('nutritionist_session_key') || '');

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('nutritionist_token');
      const storedUser = localStorage.getItem('nutritionist_user');

      if (token && storedUser) {
        try {
          // Verify token is still valid
          const userData = await authApi.getMe();
          setUser(userData);
        } catch (err) {
          // Token expired or invalid
          localStorage.removeItem('nutritionist_token');
          localStorage.removeItem('nutritionist_user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);

    // IMPORTANT: Clear all user state BEFORE login to prevent stale data flash
    // This ensures no previous user's data is shown during the login transition
    setUser(null);
    setLoggingIn(true);

    try {
      const response = await authApi.login(email, password);
      if (response.success) {
        // Generate new session key to force all components to refetch data
        const newSessionKey = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('nutritionist_token', response.token);
        localStorage.setItem('nutritionist_user', JSON.stringify(response.nutritionist));
        localStorage.setItem('nutritionist_session_key', newSessionKey);
        // Store refresh token if provided (for logout blacklisting)
        if (response.refresh_token) {
          localStorage.setItem('nutritionist_refresh_token', response.refresh_token);
        }
        setSessionKey(newSessionKey);
        setUser(response.nutritionist);
        setLoggingIn(false);
        return { success: true };
      } else {
        setError(response.error || 'Login failed');
        setLoggingIn(false);
        return { success: false, error: response.error };
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed. Please try again.';
      setError(errorMessage);
      setLoggingIn(false);
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = useCallback(async () => {
    // Get refresh token before clearing storage
    const refreshToken = localStorage.getItem('nutritionist_refresh_token');

    // Call backend to blacklist the token (don't await - we proceed with logout regardless)
    if (refreshToken) {
      authApi.logout(refreshToken).catch(() => {
        // Ignore errors - we're logging out anyway
      });
    }

    // Clear auth data
    localStorage.removeItem('nutritionist_token');
    localStorage.removeItem('nutritionist_user');
    localStorage.removeItem('nutritionist_refresh_token');

    // Clear any cached nutritionist portal data to prevent data leakage between accounts
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('nutritionist_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Clear sessionStorage as well
    sessionStorage.clear();

    setUser(null);

    // Force page reload to clear all React component state and ensure fresh data on next login
    window.location.href = '/login';
  }, []);

  const value = {
    user,
    loading,
    loggingIn, // True during login transition to show loading state
    error,
    login,
    logout,
    isAuthenticated: !!user,
    sessionKey, // Use this to trigger data refresh on login
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
